import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import { IconCheck } from "@tabler/icons-react";

import "./CodeInput.scss";

const EASE = [0.4, 0, 0.2, 1];

function cleanDigits(value, length) {
    return String(value ?? "").replace(/\D/g, "").slice(0, length);
}

function slotsFrom(digits, length) {
    return Array.from({ length }, (_, i) => digits[i] ?? "");
}

function firstEmpty(slots, from = 0) {
    for (let i = from; i < slots.length; i += 1) {
        if (!slots[i]) return i;
    }
    return -1;
}

// Where the caret belongs after a square was filled up to `cursor`: the next
// hole ahead, else the first hole behind, else the last square.
function nextCaret(slots, cursor) {
    const ahead = firstEmpty(slots, cursor);
    if (ahead !== -1) return ahead;
    const behind = firstEmpty(slots, 0);
    if (behind !== -1) return behind;
    return slots.length - 1;
}

/**
 * Input.Code — a one-time-code field: one square per digit, numeric only.
 *
 * Typing advances, Backspace steps back, arrows move, a paste (or the mobile
 * one-time-code autofill) distributes its digits. Clearing a square in the
 * middle leaves a hole instead of pulling the tail left, so a single digit can
 * be corrected in place. `onComplete` fires once per distinct full value.
 * `status` drives the visuals: `error` paints the squares in --error and
 * shakes them; `success` plays the collapse — the squares meet at the center
 * and give way to one --success-bg circle holding a check, then a ring grows
 * out of it and fades (~0.65 s in all, no bounce). With prefers-reduced-motion
 * the motion is skipped and the final state shown.
 *
 * @param {number}   [length=6]
 * @param {string}   value                 the digits, joined (holes collapse)
 * @param {function} onChange              next value (string of digits)
 * @param {function} [onComplete]          called with the full value
 * @param {'idle'|'error'|'success'} [status='idle']
 * @param {boolean}  [disabled]
 * @param {boolean}  [autoFocus]           focuses the first square once it is enabled
 * @param {string}   [aria-label]          group label (defaults to the fragments copy)
 * @param {string}   [aria-describedby]    id of the status line, set on every square
 *
 * Ref API: `focus(index?)` focuses a square (first empty by default).
 */
const CodeInput = forwardRef(function CodeInput(
    {
        length = 6,
        value = "",
        onChange,
        onComplete,
        status = "idle",
        disabled = false,
        autoFocus = false,
        className = "",
        "aria-label": ariaLabel,
        "aria-describedby": ariaDescribedBy,
        ...props
    },
    ref,
) {
    const { t } = useTranslation("fragments");
    const reduceMotion = useReducedMotion();

    const containerRef = useRef(null);
    const inputRefs = useRef([]);
    const lastCompleted = useRef(null);
    const autoFocused = useRef(false);
    const [offsets, setOffsets] = useState(null);

    // The squares are the source of truth: a cleared middle square stays empty
    // instead of the tail sliding left. `value` is their joined digits — when
    // the parent hands back what we emitted the slots are kept (holes and
    // all); when it sets something else (a clear after a wrong code) they
    // follow it.
    const [slots, setSlots] = useState(() => slotsFrom(cleanDigits(value, length), length));
    const slotsRef = useRef(slots);
    slotsRef.current = slots;

    const clean = slots.join("");
    const success = status === "success";
    const error = status === "error";
    const locked = disabled || success;

    const focusIndex = useCallback(
        (index) => {
            const el = inputRefs.current[Math.max(0, Math.min(index, length - 1))];
            if (!el) return;
            el.focus();
            el.select?.();
        },
        [length],
    );

    const focusInside = useCallback(() => {
        const root = containerRef.current;
        return !!root && root.contains(document.activeElement);
    }, []);

    useImperativeHandle(
        ref,
        () => ({
            focus: (index) => focusIndex(index ?? nextCaret(slotsRef.current, 0)),
            blur: () => inputRefs.current.forEach((el) => el?.blur()),
        }),
        [focusIndex],
    );

    // Autofocus once, as soon as the field can take focus — it may mount
    // disabled while the code is still on its way.
    useEffect(() => {
        if (!autoFocus || autoFocused.current || locked) return;
        autoFocused.current = true;
        focusIndex(0);
    }, [autoFocus, locked, focusIndex]);

    // External value change: adopt it and, if the caret is inside, put it back
    // on the first empty square (an external clear returns to the start).
    useEffect(() => {
        const incoming = cleanDigits(value, length);
        if (slotsRef.current.join("") === incoming) return;
        const next = slotsFrom(incoming, length);
        slotsRef.current = next;
        setSlots(next);
        if (!locked && focusInside()) focusIndex(nextCaret(next, 0));
    }, [value, length, locked, focusInside, focusIndex]);

    useEffect(() => {
        if (clean.length === length) {
            if (lastCompleted.current !== clean) {
                lastCompleted.current = clean;
                onComplete?.(clean);
            }
        } else {
            lastCompleted.current = null;
        }
    }, [clean, length, onComplete]);

    // Where each square must travel to meet at the row's center.
    useLayoutEffect(() => {
        if (!success) {
            setOffsets(null);
            return;
        }
        const root = containerRef.current;
        if (!root) return;
        const rect = root.getBoundingClientRect();
        const center = rect.left + rect.width / 2;
        setOffsets(
            inputRefs.current.map((el) => {
                const box = el?.parentElement?.getBoundingClientRect();
                return box ? center - (box.left + box.width / 2) : 0;
            }),
        );
    }, [success]);

    const commit = (next, caret) => {
        slotsRef.current = next;
        setSlots(next);
        onChange?.(next.join(""));
        if (typeof caret === "number") focusIndex(caret);
    };

    const handleChange = (index, event) => {
        if (locked) return;
        const current = slotsRef.current;
        let typed = event.target.value.match(/\d/g) || [];
        const next = [...current];

        if (typed.length === 0) {
            next[index] = "";
            commit(next);
            return;
        }

        // Focus selects the square's digit, so a keystroke normally arrives
        // alone. Two digits with the old one at either end means the selection
        // was lost and the person typed over it: keep only the new digit. More
        // than that is a paste or the one-time-code autofill.
        if (typed.length === 2 && current[index]) {
            if (typed[0] === current[index]) typed = [typed[1]];
            else if (typed[1] === current[index]) typed = [typed[0]];
        }

        let cursor = index;
        for (const digit of typed) {
            if (cursor >= length) break;
            next[cursor] = digit;
            cursor += 1;
        }
        commit(next, nextCaret(next, cursor));
    };

    const handleKeyDown = (index, event) => {
        if (locked) return;
        const { key } = event;
        const current = slotsRef.current;

        if (key === "Backspace") {
            event.preventDefault();
            const next = [...current];
            if (next[index]) {
                // Clear this square only; the caret stays to retype it.
                next[index] = "";
                commit(next);
            } else if (index > 0) {
                next[index - 1] = "";
                commit(next, index - 1);
            }
            return;
        }
        if (key === "Delete") {
            event.preventDefault();
            const next = [...current];
            next[index] = "";
            commit(next);
            return;
        }
        if (key === "ArrowLeft") {
            event.preventDefault();
            focusIndex(index - 1);
            return;
        }
        if (key === "ArrowRight") {
            event.preventDefault();
            focusIndex(index + 1);
            return;
        }
        if (key.length === 1 && !/\d/.test(key) && !event.metaKey && !event.ctrlKey && !event.altKey) {
            event.preventDefault();
        }
    };

    const handlePaste = (event) => {
        if (locked) return;
        const pasted = (event.clipboardData?.getData("text") || "").match(/\d/g);
        if (!pasted) return;
        event.preventDefault();
        const next = slotsFrom(pasted.slice(0, length), length);
        commit(next, nextCaret(next, pasted.length));
    };

    const rootClassName = [
        "code-input",
        success ? "is-success" : "",
        error ? "is-error" : "",
        disabled ? "is-disabled" : "",
        reduceMotion ? "is-static" : "",
        className,
    ]
        .filter(Boolean)
        .join(" ");

    const squareAnimate = (index) => {
        if (!success) return { x: 0, scale: 1, opacity: 1 };
        if (reduceMotion) return { x: 0, scale: 1, opacity: 0 };
        return { x: offsets?.[index] ?? 0, scale: 0.86, opacity: 0 };
    };

    return (
        <div
            ref={containerRef}
            className={rootClassName}
            role="group"
            aria-label={ariaLabel || t("input.code.label")}
            {...props}
        >
            <div className="code-input__squares">
                {slots.map((digit, index) => (
                    <motion.div
                        key={index}
                        className={`code-input__square${digit ? " is-filled" : ""}`}
                        initial={false}
                        animate={squareAnimate(index)}
                        transition={
                            reduceMotion
                                ? { duration: 0 }
                                : {
                                    duration: 0.2,
                                    ease: EASE,
                                    opacity: { duration: 0.12, delay: 0.1, ease: EASE },
                                }
                        }
                    >
                        <input
                            ref={(el) => {
                                inputRefs.current[index] = el;
                            }}
                            className="code-input__field"
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            autoComplete={index === 0 ? "one-time-code" : "off"}
                            aria-label={t("input.code.digit", { index: index + 1, total: length })}
                            aria-invalid={error || undefined}
                            aria-describedby={ariaDescribedBy}
                            value={digit}
                            disabled={locked}
                            onChange={(e) => handleChange(index, e)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            onPaste={handlePaste}
                            onFocus={(e) => e.target.select()}
                        />
                    </motion.div>
                ))}
            </div>

            {success && (
                <div className="code-input__success" aria-hidden="true">
                    <motion.span
                        className="code-input__ring"
                        initial={reduceMotion ? false : { scale: 1, opacity: 0 }}
                        animate={
                            reduceMotion
                                ? { scale: 1, opacity: 0 }
                                : { scale: [1, 1, 1, 1.8], opacity: [0, 0, 0.7, 0] }
                        }
                        transition={
                            reduceMotion
                                ? { duration: 0 }
                                : { duration: 0.66, times: [0, 0.56, 0.6, 1], ease: "easeOut" }
                        }
                    />
                    <motion.span
                        className="code-input__check"
                        initial={reduceMotion ? false : { scale: 0.6, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={
                            reduceMotion
                                ? { duration: 0 }
                                : { duration: 0.2, delay: 0.18, ease: EASE }
                        }
                    >
                        <motion.span
                            className="code-input__check-icon"
                            initial={reduceMotion ? false : { scale: 0.5, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={
                                reduceMotion
                                    ? { duration: 0 }
                                    : { duration: 0.18, delay: 0.3, ease: EASE }
                            }
                        >
                            <IconCheck />
                        </motion.span>
                    </motion.span>
                </div>
            )}
        </div>
    );
});

export default CodeInput;
